import { Box, Button, Tooltip, Typography } from '@mui/material';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { matchPath } from 'react-router';
import { Dot } from 'lucide';
import Icon from 'common/components/Icon';
import LinkPM from '../../common/components/LinkPM';
import useGeneralHook from '../../common/hook/useGeneralHook';
import { IRouteObject } from '../../common/types';
import { SHELL } from 'config/setupTheme';

interface Props {
  data: IRouteObject;
  pathname: string;
  open?: boolean;
}

const DefaultAsideItem: React.FC<Props> = (props: Props) => {
  const { data, pathname, open } = props;
  const { isMobile } = useGeneralHook();

  const match = matchPath(data.path?.includes('/*') ? data.path : data.path + '/*' || '', pathname);

  const checkIsActive = !!match;

  if (data.hidden || !data.path) {
    return null;
  }

  return (
    <LinkPM
      to={{
        pathname: data.absolutePath || data.path,
      }}
      color="inherit"
      style={{ display: 'flex', flex: 1, color: 'inherit' }}
    >
      <Tooltip
        title={
          !open && (
            <Typography color="inherit" noWrap>
              {data.title && <FormattedMessage id={data.title} />}
            </Typography>
          )
        }
        placement="right"
      >
        <Button
          variant="text"
          color="inherit"
          fullWidth
          disableElevation
          sx={{
            borderRadius: '10px',
            color: SHELL.sidebarText,
            bgcolor: checkIsActive ? 'rgba(255, 255, 255, 0.16)' : 'transparent',
            boxShadow: checkIsActive ? 'inset 3px 0 0 #fff' : 'none',
            ':hover': {
              bgcolor: checkIsActive ? 'rgba(255, 255, 255, 0.22)' : 'rgba(255, 255, 255, 0.1)',
            },
            minWidth: '-webkit-fill-available',
          }}
          style={{
            padding: isMobile ? '8px' : '8px 10px',
            justifyContent: open ? 'flex-start' : 'center',
          }}
        >
          <Box
            sx={{
              width: 22,
              height: 22,
              mr: open ? 1.25 : 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              color: 'inherit',
              '& svg': { width: 20, height: 20 },
            }}
          >
            {data.icon ? (
              data.icon
            ) : (
              <Icon icon={Dot} size={20} strokeWidth={checkIsActive ? 6 : 4} />
            )}
          </Box>
          <Typography
            noWrap
            sx={{
              maxWidth: open ? 180 : 0,
              opacity: open ? 1 : 0,
              overflow: 'hidden',
              fontSize: 15,
              fontWeight: checkIsActive ? 700 : 600,
              color: 'inherit',
              lineHeight: 1.5,
              py: '1px',
              transition: 'opacity .2s',
            }}
          >
            {data.title && <FormattedMessage id={data.title} />}
          </Typography>
        </Button>
      </Tooltip>
    </LinkPM>
  );
};

const DefaultAsideItems: React.FC<Props> = (props: Props) => {
  const { data, pathname, open } = props;

  if (data.hidden || !data.path) {
    return null;
  }
  if (
    data.children &&
    (data.children?.length === 1 ? !data.children[0]?.index : data.children?.length > 0)
  ) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
        }}
      >
        <Box
          className="aside-bar-title"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 0.75,
            pt: 0.5,
            pb: 0.75,
            minWidth: 0,
          }}
        >
          <Box
            aria-hidden
            sx={{
              width: 14,
              height: 2,
              borderRadius: 1,
              bgcolor: SHELL.accent,
              flexShrink: 0,
            }}
          />
          <Typography
            noWrap
            sx={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: '#E7F2F8',
              lineHeight: 1.6,
              py: '2px',
            }}
          >
            {typeof data.title === 'string' ? <FormattedMessage id={data.title} /> : data.title}
          </Typography>
          <Box
            aria-hidden
            sx={{
              flex: 1,
              minWidth: 16,
              height: '1px',
              background: 'linear-gradient(90deg, rgba(255,255,255,0.32), rgba(255,255,255,0))',
            }}
          />
        </Box>

        {data.children?.map((item, index) => {
          return <DefaultAsideItem key={index} data={item} pathname={pathname} open={open} />;
        })}
      </Box>
    );
  }
  return <DefaultAsideItem data={data} pathname={pathname} open={open} />;
};

export default DefaultAsideItems;
